import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface Category {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  sort_order: number;
}

export interface ArticleType {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
}

export interface Article {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  category_id: string | null;
  article_type_id: string | null;
  published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  featured_image: string | null;
}

export interface ArticleWithCategory extends Article {
  category: Category | null;
  article_type: ArticleType | null;
}

export function useCategories() {
  return useQuery({
    queryKey: ["categories"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as Category[];
    },
  });
}

export function useArticles(categoryId?: string) {
  return useQuery({
    queryKey: ["articles", categoryId],
    queryFn: async () => {
      let query = supabase
        .from("articles")
        .select("*, category:categories(*), article_type:article_types(*)")
        .eq("published", true)
        .order("sort_order", { ascending: true });

      if (categoryId) {
        query = query.eq("category_id", categoryId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as ArticleWithCategory[];
    },
  });
}

export function useArticleBySlug(slug: string) {
  return useQuery({
    queryKey: ["article", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*, category:categories(*), article_type:article_types(*)")
        .eq("slug", slug)
        .eq("published", true)
        .single();

      if (error) throw error;
      return data as ArticleWithCategory;
    },
    enabled: !!slug,
  });
}

export function useArticleTypes() {
  return useQuery({
    queryKey: ["article-types"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("article_types")
        .select("*")
        .order("sort_order", { ascending: true });

      if (error) throw error;
      return data as ArticleType[];
    },
  });
}

export function useArticleTypeBySlug(slug: string) {
  return useQuery({
    queryKey: ["article-type", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("article_types")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (error) throw error;
      return data as ArticleType | null;
    },
    enabled: !!slug,
  });
}

export function useArticlesByType(typeSlug: string) {
  return useQuery({
    queryKey: ["articles-by-type", typeSlug],
    queryFn: async () => {
      // First get the article type
      const { data: articleType, error: typeError } = await supabase
        .from("article_types")
        .select("id")
        .eq("slug", typeSlug)
        .maybeSingle();

      if (typeError) throw typeError;
      if (!articleType) return [];

      // Then get articles for that type
      const { data: articles, error: articlesError } = await supabase
        .from("articles")
        .select("*, category:categories(*), article_type:article_types(*)")
        .eq("article_type_id", articleType.id)
        .eq("published", true)
        .order("sort_order", { ascending: true });

      if (articlesError) throw articlesError;
      return articles as ArticleWithCategory[];
    },
    enabled: !!typeSlug,
  });
}

export function useCategoryBySlug(slug: string) {
  return useQuery({
    queryKey: ["category", slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("categories")
        .select("*")
        .eq("slug", slug)
        .single();

      if (error) throw error;
      return data as Category;
    },
    enabled: !!slug,
  });
}

export function useArticlesByCategory(categorySlug: string) {
  return useQuery({
    queryKey: ["articles-by-category", categorySlug],
    queryFn: async () => {
      // First get the category
      const { data: category, error: categoryError } = await supabase
        .from("categories")
        .select("id")
        .eq("slug", categorySlug)
        .single();

      if (categoryError) throw categoryError;

      // Then get articles for that category
      const { data: articles, error: articlesError } = await supabase
        .from("articles")
        .select("*, category:categories(*), article_type:article_types(*)")
        .eq("category_id", category.id)
        .eq("published", true)
        .order("sort_order", { ascending: true });

      if (articlesError) throw articlesError;
      return articles as ArticleWithCategory[];
    },
    enabled: !!categorySlug,
  });
}

export function usePopularArticles(limit = 4) {
  return useQuery({
    queryKey: ["popular-articles", limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("articles")
        .select("*, category:categories(*), article_type:article_types(*)")
        .eq("published", true)
        .order("view_count", { ascending: false })
        .limit(limit);

      if (error) throw error;
      return data as ArticleWithCategory[];
    },
  });
}
